<template>
  <v-card class="workspace-health-widget" outlined>
    <div v-if="widgetDataValue.loading" class="pa-6">
      <v-skeleton-loader type="subtitle" class="bg-transparent"></v-skeleton-loader>
      <v-skeleton-loader type="image" height="160" class="mt-4 bg-transparent"></v-skeleton-loader>
      <v-skeleton-loader type="text@2" class="mt-4 bg-transparent"></v-skeleton-loader>
    </div>
    <template v-else-if="widgetDataValue.data">
      <v-card-title class="workspace-health-widget__title">
        <span class="subtitle1">
          {{ $t(`dashboard.securityCoverageWidgets.${widgetType}.title`) }}
        </span>
        <v-spacer></v-spacer>
        <v-tooltip open-delay="300" location="start" content-class="menu-tooltip" :width="275">
          <template #activator="{ props }">
            <v-icon v-bind="props" icon="$info" size="24"></v-icon>
          </template>
          <span
            class="caption"
            v-html="$t(`dashboard.securityCoverageWidgets.${widgetType}.tooltip`)"
          ></span>
        </v-tooltip>
      </v-card-title>

      <v-card-text class="pa-6">
        <div class="workspace-health-widget__chart-container">
          <div class="circular-progress-container">
            <div class="progress-circle">
              <div class="progress-center"></div>
            </div>
            <div class="progress-text">
              <span class="score text-primary big-number">{{ widgetDataValue.data?.score }}</span>
              <span class="total body3">/100</span>
            </div>
          </div>
        </div>

        <div class="progress-bars-container">
          <div
            v-for="item in progressItems"
            :key="item.id"
            class="progress-bar-item"
            :data-testid="`workspace-health-${item.id}-progress-bar`"
          >
            <span class="body2 text-primary progress-bar-label">{{ item.label }}</span>
            <div class="progress-bar-content">
              <div class="progress-bar-track">
                <div class="progress-bar-fill" :style="{ width: item.value + '%' }"></div>
              </div>
              <span class="subtitle2 text-primary progress-bar-value">{{ item.value }}%</span>
            </div>
          </div>
        </div>
      </v-card-text>
    </template>

    <widget-error-state
      v-else
      :widget-type="widgetType"
      :title="$t(`dashboard.securityCoverageWidgets.${widgetType}.error`)"
      @retry-click="$emit('retry-fetch-data')"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent, computed, type PropType } from "vue";
import { DoughnutChartColors } from "@/constants/charts.ts";
import type { DataValue } from "@/types.ts";
import type { WorkspaceHealthScoreWidgetData } from "@/_store/dashboard/dashboard.module.ts";
import WidgetErrorState from "@/components/dashboard/WidgetErrorState.vue";
import { SecurityCoverageWidgetType } from "@/constants/dashboard.ts";
import { useI18n } from "vue-i18n";

export default defineComponent({
  components: { WidgetErrorState },
  props: {
    widgetDataValue: {
      type: Object as PropType<DataValue<WorkspaceHealthScoreWidgetData>>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();
    const widgetType = SecurityCoverageWidgetType.WORKSPACE_HEALTH;
    const gradientColorFrom = DoughnutChartColors.PINK;
    const gradientColorTo = DoughnutChartColors.BLUE;
    const unfilledAreaColor = `rgb(var(--v-theme-indigo-pale))`;
    const widgetPayload = computed(() => props.widgetDataValue.data);

    const clampPercentage = (value: number | null | undefined): number => {
      const num = value ?? 0;
      return Math.max(0, Math.min(100, num));
    };

    const progressAngle = computed(() => {
      const clampedScore = clampPercentage(widgetPayload.value?.score);
      const angle = (clampedScore / 100) * 360;
      return `${angle}deg`;
    });

    const progressItems = computed(() => [
      {
        id: "tickets-management",
        label: i18n.t("dashboard.securityCoverageWidgets.workspaceHealthWidget.ticketsManagement"),
        value: clampPercentage(widgetPayload.value?.ticketsManagementPercent),
      },
      {
        id: "security-gaps",
        label: i18n.t("dashboard.securityCoverageWidgets.workspaceHealthWidget.securityGaps"),
        value: clampPercentage(widgetPayload.value?.securityGapsPercent),
      },
    ]);

    return {
      widgetType,
      progressAngle,
      gradientColorFrom,
      gradientColorTo,
      unfilledAreaColor,
      progressItems,
    };
  },
});
</script>

<style scoped lang="scss">
.workspace-health-widget {
  &__title {
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgb(var(--v-theme-indigo-faint));
    padding: 12px 24px;
  }

  &__chart-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 140px;
  }
}

.v-tooltip :deep(.menu-tooltip.v-overlay__content) {
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: calc(100% + 6px);
    margin-top: -7px;
    border-width: 7px;
    border-style: solid;
    border-color: transparent transparent transparent rgb(var(--v-theme-primary));
    transform: rotate(-90deg);
  }
}

.circular-progress-container {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 140px; // ensure it doesn't grow too large
  aspect-ratio: 1 / 1;
}

.progress-circle {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  // The magic is here! conic-gradient draws a gradient around a center point.
  // We use v-bind() to connect our script variables to the CSS.
  background: conic-gradient(
    from 0deg,
    v-bind(gradientColorFrom),
    v-bind(gradientColorTo) v-bind(progressAngle),
    v-bind(unfilledAreaColor) v-bind(progressAngle),
    v-bind(unfilledAreaColor) 360deg
  );
}

.progress-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%; // This creates the 80% cutout for the doughnut effect
  height: 80%;
  background-color: rgb(var(--v-theme-surface));
  border-radius: 50%;
}

.progress-text {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.progress-bars-container {
  display: flex;
  flex-direction: column;
  gap: 4px; /* Increased space between each progress bar item */
  margin-top: 24px;
}

.progress-bar-item {
  display: flex;
  flex-direction: column;
  gap: 8px; /* Space between the label and the bar */
}

.progress-bar-content {
  display: flex;
  align-items: center;
  gap: 8px; /* Space between the progress bar and the percentage value */
}

.progress-bar-track {
  flex-grow: 1; /* Allows the track to fill the available space */
  height: 6px;
  background-color: rgb(var(--v-theme-indigo-pale));
  border-radius: 9999px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 9999px;
  background: linear-gradient(to right, v-bind(gradientColorFrom), v-bind(gradientColorTo));
  transition: width 0.3s ease-in-out;
}

.progress-bar-value {
  white-space: nowrap; /* Prevents the text like "100%" from wrapping */
  min-width: 40px;
  text-align: left;
}
</style>
